'use strict'

// https://www.vuemastery.com/courses/mastering-vuex/intro-to-vuex/
// https://www.youtube.com/watch?v=81fUhMyQbYs
import Vue from 'vue'
import Vuex from 'vuex'
import ky from 'ky'
import env from './env'
import { cloneDeep, assignIn, merge } from 'lodash'
// import pMap from 'p-map'
import * as faunaQueries from './faunaQueries'
import router from './router'
import faunadb from 'faunadb'
const faunaParseJSON = require('../node_modules/faunadb/src/_json').parseJSON

function getCookie(name) {
   //- src: https://stackoverflow.com/a/15724300/1971662
   var value = '; ' + document.cookie
   var parts = value.split('; ' + name + '=')
   if (parts.length === 2) return parts.pop().split(';').shift()
}

const authRelatedInitialState = Object.freeze({
   addTeamTempState: Object.freeze({}),
   makerState: Object.freeze({}),
   authSecret: undefined,
   authClient: undefined,
   isAuthenticated: false,
   emailVerificationEmailResent: false,
   userIP: undefined,
})

// TODO: consider breaking out `state`, `getters`, `mutations`, and `actions` into constants to then pass into the creation of the store
// for an example, see: https://markus.oberlehner.net/blog/combining-graphql-and-vuex/ (scroll 65% down page for example)

Vue.use(Vuex)
export default new Vuex.Store({
   debug: true, // TODO: only set this to true when in develpoment mode
   state: { ...cloneDeep(authRelatedInitialState) },
   getters: {
      isAuthenticated(state) {
         return !!state.makerState
      },
      getTeamProfileBySlug: state => slug => {
         if (!state.makerState.teamProfiles) return
         return state.makerState.teamProfiles.find(profile => profile.data.slug.current === slug)
      },
      getCompanyBySlug: state => slug => {
         if (!state.makerState.companies) return
         return state.makerState.companies.find(co => co.data.slug.current === slug)
      },
   },
   mutations: {
      updateStateOnLogout(state) {
         // Note: This is the only way I could figure out to effectively "reset" the maker-related state
         // while still being DRY. The limitations are due to Vue's reactivity cavets.
         Object.keys(authRelatedInitialState).forEach(stateProp => {
            Vue.set(state, stateProp, cloneDeep(authRelatedInitialState[stateProp]))
         })
      },
      setAuthTrue(state) {
         state.isAuthenticated = true
      },
      setAuthClient(state, payload) {
         state.authSecret = payload.secret
         state.authClient = new faunadb.Client({ secret: state.authSecret })
      },
      setMaker(state, payload) {
         // localStorage.setItem('meEmail', payload.data.email)
         state.makerState.maker = payload
      },
      updateMaker(state, payload) {
         // On whether to use `assign`, `assignIn`, or `merge` (other?) here, see:
         // https://stackoverflow.com/questions/19965844/lodash-difference-between-extend-assign-and-merge
         state.makerState = assignIn(state.makerState, payload)
      },
      // setIP(state) {
      //    return axios({
      //       url: 'https://api.ipify.org',
      //       method: 'get',
      //    }).then(response => {
      //       state.userIP = response.data
      //    })
      // },
      setMakerTeamMembers(state, payload) {
         const team = state.makerState.teams.find(team => team.ref.id === payload.teamRefId)
         team.makersResolved = payload.makers
      },
      updateTeamProfile(state, payload) {
         const teamToUpdate = state.makerState.teams.find(team => team.ref.value.id === payload.teamRefId)
         teamToUpdate.profile = payload.updatedProfile
         return teamToUpdate.profile
      },
      updateNewTeamTempState(state, payload) {
         // On whether to use `assign`, `assignIn`, or `merge` (other?) here, see:
         // https://stackoverflow.com/questions/19965844/lodash-difference-between-extend-assign-and-merge
         state.addTeamTempState = merge(state.addTeamTempState, payload)
      },
   },
   actions: {
      onAppInit({ commit }) {
         // commit('setIP')
         if (getCookie('isMSession')) {
            commit('setAuthTrue')
            // return ky.get('http://localhost:8787/session', {
            return ky.get(`${env.apiUrl}/session`, {
               credentials: 'include'
            }).json().then(session => {
               commit('setAuthClient', { secret: session.secret })
               const parsedMaker = cloneDeep(faunaParseJSON(JSON.stringify(session.maker)))
               return Promise.resolve(commit('setMaker', parsedMaker))
            })
               .catch(err => {
                  console.error(err)
                  return
               })
         } else {
            return Promise.resolve()
         }
      },
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Auth
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      createAndLogInMaker({ dispatch }, credentials) {
         return fetch('/.netlify/functions/create-and-login-maker', {
            method: 'post',
            body: JSON.stringify({ credentials: credentials }),
         })
            .then(response => {
               // https://www.tjvantoll.com/2015/09/13/fetch-and-errors/
               if (!response.ok) {
                  throw Error(response.statusText)
               }
               return response.json()
            })
            .then(body => {
               // COMMENTED OUT TO PREVENT LOTS OF EMAILS IN DEVELOPMENT
               // Fire off a request to send the emails asyncronusly (the user doesn't need to know this is happening so we can proceed before this finishes)
               // axios({
               //    url: '/.netlify/functions/confirm-email',
               //    method: 'post',
               //    data: { email: body[0].maker.data.email },
               // })
               return dispatch('onMakerSignIn', body)
            })
      },
      async logInMaker({ dispatch }, credentials) {
         // TODO: Dynamically get either the dev or the pro domain here
         // const parsed = await ky.post('http://localhost:8787/login-maker', {
         // const parsed = await ky.post('https://api.remotefit.com/login-maker', {
         const parsed = await ky.post(`${env.apiUrl}/login-maker`, {
            json: { credentials: credentials },
            credentials: 'include' // Important! This is required for the browser to receive cookies
         }).json()

         return dispatch('onMakerSignIn', parsed)
      },
      onMakerSignIn({ commit }, response) {
         // Use Fauna's JSON parser so that `@ref`s are converted to actual `ref`s
         const parsedRespData = cloneDeep(faunaParseJSON(JSON.stringify(response)))
         return import('faunadb').then(faunadb => {
            commit('setAuthTrue')
            commit('setAuthClient', { secret: parsedRespData[0].secret, faunadb: faunadb })
            commit('setMaker', parsedRespData[0].maker)
            return router.push('/user/settings')
         })
      },
      async logOut({ commit }) {
         await ky.post(`${env.apiUrl}/logout`, {
            credentials: 'include'
         })

         return commit('updateStateOnLogout')
      },
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Account state
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      loadMakerAccount({ commit }) {
         const authClient = this.state.authClient
         return faunaQueries.getFullMakerState(authClient, this.state.makerState.maker.data.email).then(makerState => {
            if (makerState.teams && makerState.teams.length) {
               makerState.teamProfiles.forEach(teamProfile => {
                  // TODO: I'm not sure if not cloning here is a mistake or not. By not clonging, these are essentially convenience lookups. Otherwise,
                  // the state is duplicated and it's not really beneficial.
                  //
                  // teamProfile.teamResolved = cloneDeep(makerState.teams.find(team => team.ref.id === teamProfile.datia.teamRef.id))
                  // teamProfile.companyResolved = cloneDeep(makerState.companies.find(company => company.ref.id === teamProfile.data.companyRef.id))
                  teamProfile.teamResolved = makerState.teams.find(team => team.ref.id === teamProfile.data.teamRef.id)
                  teamProfile.companyResolved = makerState.companies.find(company => company.ref.id === teamProfile.data.companyRef.id)
               })
            }
            if (makerState.companies && makerState.companies.length) {
               makerState.companies.forEach(co => {
                  co.teamProfilesResolved = co.data.profileRefs.map(profileRef => {
                     // Note: Cloning here to prevent a circular JSON reference error... If this wasn't necessary I think it'd be better not to do it (see note above)
                     const clonedTeamProfile = cloneDeep(makerState.teamProfiles.find(teamProfile => teamProfile.ref.id === profileRef.id))
                     // Remove `companyResolved` to prevent circular JSON errors
                     delete clonedTeamProfile.companyResolved
                     return clonedTeamProfile
                  })
               })
            }
            return commit('updateMaker', makerState)
         })
      },
      // confirmEmail({ dispatch }, emailToConfirm) {
      //    return axios({
      //       url: '/.netlify/functions/confirm-email',
      //       method: 'post',
      //       data: { emailToConfirm: emailToConfirm },
      //    }).then(() => {
      //       return dispatch('fetchAndSetUser', emailToConfirm)
      //    })
      // },
      updateUserInfo({ commit, dispatch }, data) {
         var userCollectionPayload = data
         var emailOld = this.state.makerState.maker.data.email
         var emailNew = data.email

         if (data.email) {
            userCollectionPayload.emailVerified = false
         }

         return faunaQueries.updateUserInfo(this.state.authClient, this.state.makerState.maker.data.email, data).then(response => {
            if (data.email || data.name) {
               commit('setMaker', response)
            }

            // Send any emails notifying the user of changes
            if (emailNew) {
               dispatch('resendEmailVerificationEmail', emailNew)
            }
            if (emailNew && data.password) {
               dispatch('sendEmailAndPasswordChangedEmail', { emailOld: emailOld, emailNew: emailNew })
            } else if (emailNew) {
               dispatch('sendEmailChangedEmail', { emailOld: emailOld, emailNew: emailNew })
            } else if (data.password) {
               dispatch('sendPasswordChangedEmail', emailOld)
            }
         })
      },
      fetchMakerTeamMembers({ commit }, team) {
         return faunaQueries.getMakerTeamMembers(this.state.authClient, team.data.makerRefs).then(response => {
            commit('setMakerTeamMembers', { teamRefId: team.ref.id, makers: response })
            return Promise.resolve()
            // return dispatch('setMakerTeamMembers', response)
         })
      },
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Company Data
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      updateCompanyData({ commit, dispatch }, args) {
         return faunaQueries.updateCompany(this.state.authClient, args.companyRef, args.updateData).then(updatedCompany => {
            return dispatch('loadMakerAccount').then(() => {
               // On accessing the Vue model here: https://github.com/vuejs/vuex/issues/1399
               this._vm.$snotify.success('Company updated')
            })
         })
      },
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Team Profile
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      updateProfileDraft({ commit }, data) {
         return faunaQueries.updateProfileDraft(this.state.authClient, data.profileRef, data.profileWithUpdatedData).then(updatedProfile => {
            return Promise.resolve(commit('updateTeamProfile', { teamRefId: data.teamRef.id, updatedProfile: updatedProfile })).then(() => {
               return updatedProfile
            })
         })
      },
      publishProfile({ commit }, data) {
         const triggerBuild = () => {
            return ky.post(`${env.apiUrl}/trigger-site-rebuild`, {
               credentials: 'include'
            })
         }

         const updateFauna = () => {
            return faunaQueries.publishDraft({ client: this.state.authClient, profileRef: data.profileRef, profileWithUpdatedData: data.profileWithUpdatedData, isFirstPublish: data.isFirstPublish }).then(updatedProfile => {
               return commit('updateTeamProfile', { teamRefId: data.teamRef.id, updatedProfile: updatedProfile })
            })
         }

         // Prevent lots of rebuilds while testing other aspects of this.
         // return updateFauna().then(triggerBuild)
         return updateFauna()
      },
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // For Adding / Joining Companies
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      updateNewTeamTempState({ commit }, data) {
         return commit('updateNewTeamTempState', data)
      },
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Add & Delete Teams
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      async addTeam({ dispatch }, data) {
         await ky.post(`${env.apiUrl}/add-team`, {
            json: {
               makerRef: this.state.makerState.maker.ref,
               data
            },
            credentials: 'include' // Important! This is required for the browser to receive cookies
         }).json()

         return dispatch('loadMakerAccount')
      },
      async deleteMakerTeam({ dispatch }, { company, team }) {
         await ky.post(`${env.apiUrl}/delete-team`, {
            json: {
               makerRef: this.state.makerState.maker.ref,
               company,
               team,
               makerTeamRefs: this.state.makerState.maker.data.teamRefs,
            },
            credentials: 'include' // Important! This is required for the browser to receive cookies
         }).json()

         return dispatch('loadMakerAccount').then(() => {
            // On accessing the Vue model here: https://github.com/vuejs/vuex/issues/1399
            router.push(`/${company.data.slug.current}/manage`).then(() => this._vm.$snotify.success('Team deleted'))
         })
      },
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      // Emails
      // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
      resendEmailVerificationEmail({ }, email) {
         return ky.post('/.netlify/functions/resend-email-verification', {
            json: { email: email },
         })
      },
      sendEmailAndPasswordChangedEmail({ }, data) {
         return ky.post('/.netlify/functions/send-email-and-password-changed-email', {
            json: data,
         })
      },
      sendEmailChangedEmail({ }, data) {
         return ky.post('/.netlify/functions/send-email-changed-email', {
            json: data,
         })
      },
      sendPasswordChangedEmail({ }, email) {
         return ky.post('/.netlify/functions/send-password-changed-email', {
            json: { email: email },
         })
      },
   },
})
